import { addDaysLocal } from "@/lib/bookingDates";

export const GUEST_TYPES = [
  {
    value: "tourist",
    label: "Tourist",
    description: "Kurzaufenthalt, flexible Buchungsdauer.",
  },
  {
    value: "seasonal",
    label: "Saisoncamper",
    description: "Längerer Aufenthalt, typisch mindestens eine Saison.",
  },
  {
    value: "day_visitor",
    label: "Tagesgast",
    description: "An- und Abreise am selben Tag (1 Nacht im System).",
  },
] as const;

export type GuestTypeValue = (typeof GUEST_TYPES)[number]["value"];

export function guestTypeLabel(value: string | null | undefined): string {
  if (!value) return "—";
  if (value === "permanent") return "Gast";
  return GUEST_TYPES.find((t) => t.value === value)?.label ?? value;
}

export interface GuestTypePeriodDefaults {
  min_nights: number;
  default_nights: number;
  same_day: boolean;
}

export type GuestTypeDefaultsMap = Record<GuestTypeValue, GuestTypePeriodDefaults>;

export const DEFAULT_GUEST_TYPE_DEFAULTS: GuestTypeDefaultsMap = {
  tourist: { min_nights: 1, default_nights: 3, same_day: false },
  seasonal: { min_nights: 30, default_nights: 30, same_day: false },
  day_visitor: { min_nights: 1, default_nights: 1, same_day: true },
};

export function bookingPeriodForGuestType(
  guestType: GuestTypeValue,
  defaults: GuestTypeDefaultsMap,
  today: string,
): { arrival: string; departure: string } {
  const cfg = defaults[guestType] ?? DEFAULT_GUEST_TYPE_DEFAULTS.tourist;
  const arrival = today;
  const nights = cfg.same_day ? 1 : Math.max(cfg.default_nights, cfg.min_nights, 1);
  return { arrival, departure: addDaysLocal(arrival, nights) };
}

export function countNightsForPeriod(arrival: string, departure: string): number {
  const a = new Date(arrival);
  const d = new Date(departure);
  const diff = Math.round((d.getTime() - a.getTime()) / 86_400_000);
  return diff > 0 ? diff : 0;
}

export function guestTypeMinNightsError(
  guestType: GuestTypeValue,
  arrival: string,
  departure: string,
  defaults: GuestTypeDefaultsMap,
): string | null {
  const cfg = defaults[guestType] ?? DEFAULT_GUEST_TYPE_DEFAULTS.tourist;
  const nights = countNightsForPeriod(arrival, departure);
  if (nights < cfg.min_nights) {
    return `Für ${guestTypeLabel(guestType)} sind mindestens ${cfg.min_nights} ${
      cfg.min_nights === 1 ? "Nacht" : "Nächte"
    } erforderlich.`;
  }
  return null;
}
